import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export interface ApiError {
  status: FetchBaseQueryError['status'];
  message: string;
  field?: string;
}

const DEFAULT_ERROR_MESSAGE = 'Request failed';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function firstString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (!Array.isArray(value)) {
    return undefined;
  }

  for (const item of value) {
    const message = firstString(item);
    if (message) {
      return message;
    }
  }

  return undefined;
}

function findValidationError(
  value: unknown,
): Pick<ApiError, 'message' | 'field'> | undefined {
  if (Array.isArray(value)) {
    for (const item of value) {
      const validationError = findValidationError(item);
      if (validationError) {
        return validationError;
      }
    }

    return undefined;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  if (isRecord(value.constraints)) {
    const message = firstString(Object.values(value.constraints));
    if (message) {
      return {
        message,
        ...(typeof value.property === 'string' ? { field: value.property } : {}),
      };
    }
  }

  return findValidationError(value.children);
}

export function normalizeApiError(error: FetchBaseQueryError): ApiError {
  const responseData = 'data' in error ? error.data : undefined;

  if (isRecord(responseData)) {
    const validationError = findValidationError(responseData.message);
    if (validationError) {
      return { status: error.status, ...validationError };
    }

    const responseMessage = firstString(responseData.message);
    if (responseMessage) {
      return { status: error.status, message: responseMessage };
    }

    const responseError = firstString(responseData.error);
    if (responseError) {
      return { status: error.status, message: responseError };
    }
  }

  const transportMessage = 'error' in error ? firstString(error.error) : undefined;

  return {
    status: error.status,
    message: transportMessage ?? DEFAULT_ERROR_MESSAGE,
  };
}
